import React, { useState, useEffect } from 'react';
import {
  SearchOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  CarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  FilePdfOutlined,
  DownOutlined,
  UpOutlined
} from '@ant-design/icons';
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Typography,
  Tag,
  Tooltip,
  message,
  Modal,
  Row,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Checkbox,
  Badge,
  Grid,
  InputNumber,
  Pagination
} from 'antd';
import * as XLSX from 'xlsx';
import { getBills, deleteBill, settleBillAmount, getVehicleInsurances } from '../Services/Services';
import html2pdf from 'html2pdf.js';
import logo from '../Logo.png';
import { getVehicleInsurancesByParty } from '../Services/Services';


const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

const BillReport = ({ onEdit }) => {
  const [allVehicles, setAllVehicles] = useState([]);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [filters, setFilters] = useState({
    party_name: '',
    vehicle_no: '',
    start_date: '',
    end_date: ''
  });
  const [selectedBill, setSelectedBill] = useState(null);
  const [isViewModal, setIsViewModal] = useState(false);
  const [isSettleModal, setIsSettleModal] = useState(false);
  const [selectedBillForSettle, setSelectedBillForSettle] = useState(null);
  const [settlementAmount, setSettlementAmount] = useState(0);
  const [settling, setSettling] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [mobilePage, setMobilePage] = useState(1);
  const mobilePageSize = 5;

  const fetchBills = async () => {

    const vehicleResponse = await getVehicleInsurances({}, 1, 1000);

    setAllVehicles(vehicleResponse.results || []);



    setLoading(true);
    try {
      const params = {};
      if (filters.party_name && filters.party_name.trim()) params.party_name = filters.party_name;
      if (filters.vehicle_no && filters.vehicle_no.trim()) params.vehicle_no = filters.vehicle_no;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await getBills(params);
      const fetchedItems = response.results || [];
      setData(fetchedItems);
      console.log(fetchedItems);
      if (fetchedItems.length === 0 && !loading) message.info("No records found");
    } catch (error) {
      console.error("Fetch Error:", error);

      message.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBills();
  }, []);

  const handleReset = () => {
    setFilters({ party_name: '', vehicle_no: '', start_date: '', end_date: '' });
    setTimeout(() => fetchBills(), 100);
  };

  const handleDateRangeChange = (dates, dateStrings) => {
    setFilters({ ...filters, start_date: dateStrings[0], end_date: dateStrings[1] });
  };

  const exportToExcel = () => {
    setExportLoading(true);
    try {
      if (data.length === 0) {
        message.warning("No data to export!");
        return;
      }
      const excelData = [];
      data.forEach(bill => {
        if (bill.items && bill.items.length > 0) {
          bill.items.forEach(item => {
            excelData.push({
              "Bill ID": bill.bill_id,
              "Bill Date": bill.bill_date,
              "Party Name": bill.party_name,
              "Mobile": bill.mobile || bill.items?.[0]?.mobile || "-" || bill.items?.[0]?.mobile || "-" || "-",
              "Vehicle No": item.vehicle_no || "-",
              "Chassis No":
                allVehicles.find(
                  v => v.id === item.vehicle_id
                )?.chassisNumber || "-",
              "Amount": item.amount,
              "Service Date": item.service_date,
              "Description": item.description || "-",
              "Total Bill Amount": bill.total_amount,
              "Receipt Amount": bill.receipt_amount || 0,
              "Balance Amount": (bill.total_amount - (bill.receipt_amount || 0)).toFixed(2),
              "Status": bill.is_pending ? "Pending" : "Settled"
            });
          });
        } else {
          excelData.push({
            "Bill ID": bill.bill_id,
            "Bill Date": bill.bill_date,
            "Party Name": bill.party_name,
            "Mobile": bill.mobile || bill.items?.[0]?.mobile || "-" || bill.items?.[0]?.mobile || "-" || "-",
            "Vehicle No": "-",
            "Amount": "-",
            "Service Date": "-",
            "Description": "-",
            "Total Bill Amount": bill.total_amount,
            "Receipt Amount": bill.receipt_amount || 0,
            "Balance Amount": (bill.total_amount - (bill.receipt_amount || 0)).toFixed(2),
            "Status": bill.is_pending ? "Pending" : "Settled"
          });
        }
      });
      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Bills");
      XLSX.writeFile(wb, `Bills_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      message.success("Export successful!");
    } catch (err) {
      console.error(err);
      message.error("Export failed!");
    } finally {
      setExportLoading(false);
    }
  };

  const handleDelete = async (id, billId) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete bill ${billId}?`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        setLoading(true);
        try {
          await deleteBill(id);
          message.success(`Bill ${billId} deleted successfully`);
          await fetchBills();
        } catch (error) {
          message.error("Delete failed");
          console.error(error);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleEditClick = (bill) => {
    if (onEdit) onEdit(bill.id);
    else {
      sessionStorage.setItem('paymentId', bill.id);
      window.location.href = '/payment-in';
    }
  };

  const handleViewDetails = (bill) => {
    setSelectedBill(bill);
    setIsViewModal(true);
  };

  const handleSettleClick = (bill) => {
    setSelectedBillForSettle(bill);
    const balanceAmount = bill.total_amount - (bill.receipt_amount || 0);
    setSettlementAmount(balanceAmount);
    setIsSettleModal(true);
  };

  const handleSettlement = async () => {
    if (!settlementAmount || settlementAmount <= 0) {
      message.error("Please enter a valid settlement amount");
      return;
    }
    const balanceAmount = selectedBillForSettle.total_amount - (selectedBillForSettle.receipt_amount || 0);
    if (settlementAmount > balanceAmount) {
      message.error(`Settlement amount cannot exceed balance amount of ₹${balanceAmount.toLocaleString('en-IN')}`);
      return;
    }
    Modal.confirm({
      title: 'Confirm Settlement',
      content: `Are you sure you want to settle ₹${settlementAmount.toLocaleString('en-IN')} for bill ${selectedBillForSettle.bill_id}?`,
      okText: 'Yes',
      cancelText: 'No',
      onOk: async () => {
        setSettling(true);
        try {
          const response = await settleBillAmount(selectedBillForSettle.id, { is_pending: true });
          if (response) {
            message.success(`₹${settlementAmount.toLocaleString('en-IN')} settled successfully`);
            setIsSettleModal(false);
            setSettlementAmount(0);
            setSelectedBillForSettle(null);
            await fetchBills();
          }
        } catch (error) {
          message.error("Settlement failed");
          console.error(error);
        } finally {
          setSettling(false);
        }
      }
    });
  };

  const downloadPDF = (bill) => {
    setPdfLoading(true);

    const formatCurrency = (amount) => {
      if (isNaN(amount)) return '0.00';
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    // Number to words (Indian Rupees)
    const numberToWords = (num) => {
      if (num === 0) return 'Zero';
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
        'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      const lakhs = Math.floor(num / 100000);
      let remainder = num % 100000;
      const thousands = Math.floor(remainder / 1000);
      remainder = remainder % 1000;
      const hundreds = Math.floor(remainder / 100);
      const units = remainder % 100;

      let words = '';
      if (lakhs > 0) words += ones[lakhs] + ' Lakh ';
      if (thousands > 0) words += ones[thousands] + ' Thousand ';
      if (hundreds > 0) words += ones[hundreds] + ' Hundred ';
      if (units > 0) {
        if (units < 20) words += ones[units];
        else words += tens[Math.floor(units / 10)] + (units % 10 !== 0 ? ' ' + ones[units % 10] : '');
      }
      return words.trim() + ' Only';
    };

    // Items rows generate karo
    const itemList = bill.items || [];
    let itemsRows = '';
    itemList.forEach((item, idx) => {
      const vehicle = allVehicles.find(v => v.id === item.vehicle_id);
      const qty = item.qty || 1;
      const rate = item.rate || parseFloat(item.amount) / qty || 0;
      const amount = qty * rate;
      let descHtml = (item.description || '-').replace(/\n/g, '<br>');
      itemsRows += `
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 6px 8px; text-align: center;">${idx + 1}</td>
        <td style="padding: 6px 8px;">${item.vehicle_no || '-'}</td>
        <td style="padding: 6px 8px;">${vehicle?.chassisNumber || '-'}</td>
        <td style="padding: 6px 8px; white-space: pre-wrap;">${descHtml}</td>
        <td style="padding: 6px 8px; text-align: center;">${qty}</td>
        <td style="padding: 6px 8px; text-align: right;">₹${formatCurrency(rate)}</td>
        <td style="padding: 6px 8px; text-align: right; font-weight: 600;">₹${formatCurrency(amount)}</td>
      </tr>
    `;
    });

    // Agar koi item nahi hai to placeholder row
    if (itemList.length === 0) {
      itemsRows = `<tr><td colspan="7" style="padding: 20px; text-align: center;">No items available</td></tr>`;
    }

    // Total amount aur words
    const totalAmount = parseFloat(bill.total_amount) || 0;
    const wordsTotal = numberToWords(Math.floor(totalAmount));
    const balanceAmount = totalAmount - (parseFloat(bill.receipt_amount) || 0);

    // Invoice date format
    const invoiceDate = bill.bill_date || new Date().toLocaleDateString('en-IN');

    // HTML template (ab sab dynamic hai)
    const element = document.createElement('div');
    element.innerHTML = `
    <div style="width: 100%; max-width: 1000px; margin: 0 auto; background: #ffffff; font-family: 'Times New Roman', Times, serif; box-sizing: border-box; border: 1px solid #a3a3a3; padding: 15px; border-radius: 4px;">
      
      <div style="height: 5px; background: linear-gradient(90deg, #103f7a 75%, #1c7c34 75%); margin: -15px -15px 15px -15px; border-radius: 4px 4px 0 0;"></div>

      <!-- Header with Logo & Company Info (static, as per your brand) -->
      <div style="padding: 5px 10px 15px 10px; background: #ffffff;">
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 20px;">
          <div style="width: 85px; min-width: 85px; display: flex; align-items: center; justify-content: center;">
            <img src="${logo}" style="width:100%; height:auto;" />
          </div>
          <div style="flex:1; text-align: center;">
            <div style="font-size: 24px; font-weight: bold; color: #103f7a;">D.K. TRANSPORT CONSULTANCY</div>
            <div style="font-size: 10.5px; color: #334155;">PARKING NO. 1 BABLA FUELS KE BAJU T P NAGAR RAWABHATA RAIPUR</div>
            <div style="font-size: 10px; color: #1c7c34;">(Deal in all RTO related matters, Insurance - 2 Wheeler, 4 Wheeler, Commercial Vehicles)</div>
          </div>
          <div style="text-align: right; border-left: 1px solid #cbd5e1; padding-left: 15px; min-width: 150px; font-size: 11px;">
            <div style="font-size: 12px; font-weight: bold; color: #103f7a;">Durgesh Kumar Siware</div>
            <div>📞 79749 97280</div>
            <div>📞 73890 42205</div>
          </div>
        </div>
      </div>

      <div style="text-align: center; background: #103f7a; color: #ffffff; font-size: 13px; font-weight: bold; padding: 4px 0; margin: 5px 10px 15px 10px; letter-spacing: 3px;">
        INVOICE
      </div>

      <!-- Bill To & Invoice Info (dynamic) -->
      <div style="margin: 0 10px 15px 10px; border: 1px solid #cbd5e1; border-radius: 3px;">
        <table style="width:100%; border-collapse: collapse; font-size: 11.5px;">
          <tr>
            <td style="width:55%; padding:12px; vertical-align:top; border-right:1px solid #cbd5e1;">
              <div style="font-size:10px; font-weight:bold; color:#525252;">BILL TO:</div>
              <div style="font-size:13px; font-weight:bold; color:#000000;">${bill.party_name || ''}</div>
              <div style="color:#334155;">${bill.mobile || bill.items?.[0]?.mobile || "-" || bill.items?.[0]?.mobile || "-" ? `📱 ${bill.mobile || bill.items?.[0]?.mobile || "-"}` : ''}</div>
              <!-- Agar party ka address bhi ho to yahan add kar sakte ho -->
            </td>
            <td style="width:45%; vertical-align:top;">
              <table style="width:100%; border-collapse:collapse;">
                <tr style="border-bottom:1px solid #cbd5e1;">
                  <td style="padding:8px 12px;">Mobile No</td>
                  <td style="padding:8px 12px; text-align:right; font-weight:bold;">${bill.mobile || bill.items?.[0]?.mobile || "-" || bill.items?.[0]?.mobile || "-" || '-'}</td>
                </tr>
                <tr style="border-bottom:1px solid #cbd5e1;">
                  <td style="padding:8px 12px;">Invoice No</td>
                  <td style="padding:8px 12px; text-align:right; font-weight:bold; color:#103f7a;">${bill.bill_id}</td>
                </tr>
                <tr>
                  <td style="padding:8px 12px;">Date</td>
                  <td style="padding:8px 12px; text-align:right; font-weight:bold;">${invoiceDate}</td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </div>

      <!-- Items Table (dynamic rows) -->
      <div style="margin: 0 10px 15px 10px;">
        <table style="width:100%; border-collapse: collapse; font-size: 11.5px; border:1px solid #a3a3a3;">
          <thead>
            <tr style="background:#103f7a; color:#ffffff;">
              <th style="padding:8px 5px; text-align:center;">#</th>
              <th style="padding:8px 5px; text-align:left;">Vehicle No</th>
              <th style="padding:8px 5px; text-align:left;">Chassis No</th>
              <th style="padding:8px 5px; text-align:left;">Description</th>
              <th style="padding:8px 5px; text-align:center;">Qty</th>
              <th style="padding:8px 5px; text-align:right;">Rate</th>
              <th style="padding:8px 5px; text-align:right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${itemsRows}
            <!-- Total Row -->
            <tr style="background:#f0f7ff; border-top:2px solid #a3a3a3;">
              <td colspan="6" style="padding:8px 10px; text-align:right; font-weight:bold;">TOTAL AMOUNT</td>
              <td style="padding:8px 10px; text-align:right; font-weight:bold; color:#103f7a;">₹${formatCurrency(totalAmount)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Amount in Words -->
      <div style="margin: 0 10px 15px 10px; padding: 8px 12px; background: #f4fdf8; border: 1px solid #bbf7d0; border-radius: 3px; font-size: 11px;">
        Amount in Words: <span style="text-transform: uppercase; font-weight: bold;">${wordsTotal}</span>
      </div>

      <!-- Bank Details (static) -->
      <div style="margin: 0 10px 20px 10px; padding: 10px 12px; background: #f7faff; border-left: 4px solid #103f7a; border-radius: 3px; font-size: 11px; display: flex; flex-wrap: wrap; justify-content: space-between;">
        <div><strong>🏦 A/C NO:</strong> 38473468220</div>
        <div><strong>🏛️ IFSC:</strong> SBIN0002852</div>
        <div><strong>👤 NAME:</strong> Durgesh Kumar Siware</div>
        <div><strong>📞 MO:</strong> 7974997280, 7389042205</div>
      </div>

      <!-- Signatures -->
      <div style="display: flex; justify-content: space-between; margin: 30px 25px 20px 25px; font-size: 11px;">
        <div style="text-align: center; width: 30%;">
          <div style="border-bottom: 1px solid #cbd5e1; height: 35px; margin-bottom: 5px;"></div>
          <div>Customer Signature</div>
        </div>
        <div style="text-align: center; width: 30%;">
          <div style="font-weight: bold; color: #103f7a; margin-bottom: 20px;">Durgesh Siware</div>
          <div style="border-top: 1px solid #cbd5e1; padding-top: 5px;">Authorized Signature</div>
        </div>
      </div>

      <!-- Footer -->
      <div style="background:#0d1e3d; width:100%; border-radius: 2px; overflow: hidden;">
        <div style="background:#103f7a; padding:8px 15px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap;">
          <div style="font-size:8.5px; color:#ffffff;">📍 NEAR PARKING NO.1 BABLA FUELS KE BAJU T P NAGAR RAWANBHATA RAIPUR 493221</div>
          <div style="background:#1c7c34; padding:3px 12px; border-radius:12px; font-size:8.5px; color:#ffffff;">✉ durgeshshivare@gmail.com</div>
        </div>
        <div style="font-size:6px; color:#4b5563; text-align:center; padding:3px; background:#020b17;">powered by yashvitech IT solution</div>
      </div>
    </div>
  `;

    const opt = {
      margin: [0.3, 0.3, 0.3, 0.3],
      filename: `Bill_${bill.bill_id}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: 'jpeg', quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, logging: false },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save()
      .then(() => {
        setPdfLoading(false);
        message.success('PDF downloaded successfully!');
      })
      .catch((error) => {
        console.error(error);
        setPdfLoading(false);
        message.error('Failed to generate PDF');
      });
  };

  // Desktop columns (same as your original table, no extra fields)
  const columns = [
    {
      title: 'Settle',
      key: 'settle_checkbox',
      width: 80,
      fixed: 'left',
      render: (_, record) => (
        <Checkbox
          checked={!record.is_pending}
          disabled={!record.is_pending}
          onChange={(e) => { if (e.target.checked && record.is_pending) handleSettleClick(record); }}
        >
          {!record.is_pending ? 'Settled' : 'Pending'}
        </Checkbox>
      )
    },
    { title: 'SR NO.', key: 'srno', width: 70, fixed: 'left', render: (_, __, index) => index + 1 },
    { title: 'Bill Date', dataIndex: 'bill_date', key: 'bill_date', width: 110, render: (text) => <Tag color="blue">{text}</Tag> },
    { title: 'Bill ID', dataIndex: 'bill_id', key: 'bill_id', width: 150, render: (text) => <Tag color="geekblue">{text}</Tag> },
    {
      title: 'Party Details', key: 'party_details', width: 180,
      render: (_, record) => (
        <div><div><strong>{record.party_name}</strong></div>{record.mobile && <div style={{ fontSize: 12, color: '#666' }}>📱 {record.mobile}</div>}</div>
      )
    },
    // {
    //   title: 'Vehicles', key: 'vehicles', width: 150,
    //   render: (_, record) => (

    //     <Space wrap size={4}>
    //       {record.items?.map((item, idx) => (
    //         <Tag key={idx} icon={<CarOutlined />} color="purple">{item.vehicle_no || `V${idx + 1}`}</Tag>

    //       ))}
    //     </Space>
    //   )
    // },
    // {
    //   title: 'Chassis No.',
    //   key: 'chassis',
    //   width: 120,
    //   render: (_, record) => (
    //     <div>
    //       {record.items?.map((item, idx) => {

    //         const vehicle = allVehicles.find(
    //           v => v.id === item.vehicle_id
    //         );

    //         return (
    //           <div key={idx}>
    //             {vehicle?.chassisNumber || '-'}
    //           </div>
    //         );
    //       })}
    //     </div>
    //   )
    // },
    {
      title: 'Bill Amount', dataIndex: 'total_amount', key: 'total_amount', width: 120,
      render: (text) => <Text strong style={{ color: '#1890ff' }}>₹{parseFloat(text).toLocaleString('en-IN')}</Text>
    },
    {
      title: 'Receipt Amount', key: 'receipt_amount', width: 120,
      render: (_, record) => <Text strong style={{ color: record.receipt_amount > 0 ? '#52c41a' : '#999' }}>₹{parseFloat(record.receipt_amount || 0).toLocaleString('en-IN')}</Text>
    },
    {
      title: 'Balance', key: 'balance', width: 120,
      render: (_, record) => {
        const balance = record.total_amount - (record.receipt_amount || 0);
        return <Text strong style={{ color: balance > 0 ? '#ff4d4f' : '#52c41a' }}>₹{balance.toLocaleString('en-IN')}</Text>;
      }
    },
    {
      title: 'Status', key: 'status', width: 100,
      render: (_, record) => <Badge status={record.is_pending ? "warning" : "success"} text={record.is_pending ? "Pending" : "Settled"} />
    },
    {
      title: 'Actions', key: 'actions', width: 160, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View"><Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(record)} /></Tooltip>
          <Tooltip title="Edit"><Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditClick(record)} /></Tooltip>
          <Tooltip title="Download PDF"><Button type="link" size="small" icon={<FilePdfOutlined />} style={{ color: '#ff4d4f' }} onClick={() => downloadPDF(record)} loading={pdfLoading} /></Tooltip>
          <Tooltip title="Delete"><Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id, record.bill_id)} /></Tooltip>
        </Space>
      )
    }
  ];

  // Mobile card: only show basic info, more button shows the rest (vehicles list and settle action, but no address/receipt amount extra)
  const renderMobileCard = (bill) => {
    const isExpanded = expandedKeys.includes(bill.id);
    const balance = bill.total_amount - (bill.receipt_amount || 0);

    return (
      <div
        className="mobile-card"
        style={{
          background: '#fff',
          borderRadius: 12,
          marginBottom: 12,
          padding: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          border: '1px solid #eee'
        }}
      >
        {/* Header: Bill ID + Date | Status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <Tag color="geekblue" style={{ fontSize: 11, padding: '0 6px', margin: 0 }}>{bill.bill_id}</Tag>
            <Tag color="blue" style={{ fontSize: 11, padding: '0 6px', margin: 0 }}>{bill.bill_date}</Tag>
          </div>
          <Badge status={bill.is_pending ? "warning" : "success"} text={bill.is_pending ? "Pending" : "Settled"} />
        </div>

        {/* Party Name + Mobile */}
        <div style={{ marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: '#1f1f1f' }}>{bill.party_name}</span>
          {bill.mobile || bill.items?.[0]?.mobile || "-" || bill.items?.[0]?.mobile || "-" && (
            <span style={{ fontSize: 12, color: '#888', marginLeft: 10 }}>📱 {bill.mobile || bill.items?.[0]?.mobile || "-"}</span>
          )}
        </div>

        {/* Amounts in one line to save space */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 13 }}>
          <span>Total: <strong style={{ color: '#1890ff' }}>₹{bill.total_amount.toLocaleString('en-IN')}</strong></span>
          <span>Balance: <strong style={{ color: balance > 0 ? '#ff4d4f' : '#52c41a' }}>₹{balance.toLocaleString('en-IN')}</strong></span>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
          <Space size={4}>
            <Tooltip title="View"><Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handleViewDetails(bill)} /></Tooltip>
            <Tooltip title="Edit"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditClick(bill)} /></Tooltip>
            <Tooltip title="PDF"><Button type="text" size="small" icon={<FilePdfOutlined />} style={{ color: '#ff4d4f' }} onClick={() => downloadPDF(bill)} /></Tooltip>
            <Tooltip title="Delete"><Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(bill.id, bill.bill_id)} /></Tooltip>
          </Space>
          <Button
            type="link"
            size="small"
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => {
              if (isExpanded) setExpandedKeys(expandedKeys.filter(k => k !== bill.id));
              else setExpandedKeys([...expandedKeys, bill.id]);
            }}
            style={{ fontSize: 12, padding: '0 4px' }}
          >
            {isExpanded ? 'Less' : 'More'}
          </Button>
        </div>

        {/* Expanded Details - compact */}
        {isExpanded && (
          <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>Vehicles & Services</div>
            {bill.items?.length > 0 ? (
              bill.items.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    background: '#fafafa',
                    borderRadius: 8,
                    padding: '8px',
                    marginBottom: 8,
                    fontSize: 12,
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ color: '#666', minWidth: 70 }}>Vehicle:</span>
                    <span style={{ fontWeight: 500 }}>{item.vehicle_no || '-'}</span>
                  </div>

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ color: '#666', minWidth: 70 }}>Chassis:</span>
                    <span>
                      {
                        allVehicles.find(
                          v => v.id === item.vehicle_id
                        )?.chassisNumber || '-'
                      }
                    </span>
                  </div>


                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ color: '#666', minWidth: 70 }}>Amount:</span>
                    <span>₹{parseFloat(item.amount).toLocaleString('en-IN')}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ color: '#666', minWidth: 70 }}>Service Date:</span>
                    <span>{item.service_date || '-'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{ color: '#666', minWidth: 70 }}>Description:</span>
                    <span>{item.description || '-'}</span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ fontSize: 12, color: '#999', padding: '8px', background: '#fafafa', borderRadius: 8, textAlign: 'center' }}>
                No items
              </div>
            )}
            {bill.is_pending && (
              <Button
                size="small"
                type="primary"
                danger
                icon={<DollarOutlined />}
                onClick={() => handleSettleClick(bill)}
                style={{ marginTop: 10, width: '100%', borderRadius: 8, fontWeight: 500 }}
              >
                Settle Now
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Custom CSS (as provided by you)
  const customStyles = `
    .report-container {
      padding: 15px 25px;
      background-color: #f8fafc;
      min-height: 100vh;
      font-family: 'Inter', sans-serif;
    }
    .filter-card {
      background: white;
      padding: 20px;
      border-radius: 10px;
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      gap: 35px;
      margin-bottom: 20px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.04);
      flex-wrap: wrap;
    }
    .filter-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
      flex: 1;
      min-width: 150px;
    }
    .filter-group label {
      font-size: 11px;
      font-weight: 600;
      color: #64748b;
      text-transform: uppercase;
    }
    .filter-group input {
      height: 38px;
      border: 1.5px solid #e2e8f0;
      border-radius: 6px;
      padding: 0 10px;
      font-size: 13px;
      outline: none;
    }
    .rto-table-card {
      background: white;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
      overflow: auto;
      max-height: 75vh;
    }
    .rto-table {
      width: 100%;
      border-collapse: collapse;
    }
    .rto-table th {
      background: #1e293b;
      color: white;
      padding: 10px 15px;
      text-align: left;
      position: sticky;
      top: 0;
      font-size: 12px;
      font-weight: 500;
    }
    .rto-table td {
      padding: 8px 15px;
      border-bottom: 1px solid #f1f5f9;
      font-size: 13px;
      font-weight: 400;
      color: #475569;
    }
    .vehicle-badge {
      background: #f1f5f9;
      color: #1e293b;
      padding: 2px 8px;
      border-radius: 4px;
      font-weight: 400;
      border: 1px solid #e2e8f0;
      font-size: 12px;
    }
    .btn-search {
      background: #6366f1;
      color: white;
      border: none;
      border-radius: 6px;
      height: 38px;
      padding: 0 20px;
      cursor: pointer;
      font-size: 13px;
    }
    .btn-clear {
      height: 38px;
      width: 40px;
      border-radius: 6px;
      border: 1px solid #e2e8f0;
      background: white;
      color: #ef4444;
    }
    .btn-excel {
      background: #107c41;
      color: white;
      border: none;
      border-radius: 6px;
      height: 38px;
      padding: 0 15px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
    }
    @media (max-width: 768px) {
      .filter-card {
        flex-direction: column;
        align-items: stretch;
        gap: 15px;
      }
      .report-container {
        padding: 10px;
      }
    }
  `;

  return (
    <div className="report-container">
      <style>{customStyles}</style>
      {/* Filter Section */}
      <div className="filter-card">
        <div className="filter-group">
          <label>Vehicle No</label>
          <Input placeholder="Search by vehicle" value={filters.vehicle_no} onChange={(e) => setFilters({ ...filters, vehicle_no: e.target.value })} allowClear onPressEnter={fetchBills} />
        </div>
        <div className="filter-group">
          <label>Party Name</label>
          <Input placeholder="Search by party" value={filters.party_name} onChange={(e) => setFilters({ ...filters, party_name: e.target.value })} allowClear onPressEnter={fetchBills} />
        </div>
        <div className="filter-group">
          <label>Date Range</label>
          <RangePicker style={{ width: '100%' }} onChange={handleDateRangeChange} />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-search" onClick={fetchBills}><SearchOutlined /> Search</button>
          <button className="btn-clear" onClick={handleReset}>Clear</button>
          <button className="btn-excel" onClick={exportToExcel}><DownloadOutlined /> Export</button>
        </div>
      </div>

      {/* Table / Mobile Cards */}
      {!isMobile ? (
        <div className="rto-table-card">
          <Table
            columns={columns}
            dataSource={data}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10, showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} bills`, showSizeChanger: true, showQuickJumper: true }}
            bordered
            scroll={{ x: 1450 }}
          />
        </div>
      ) : (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              Loading...
            </div>
          ) : data.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              No bills found
            </div>
          ) : (
            <>
              {data
                .slice(
                  (mobilePage - 1) * mobilePageSize,
                  mobilePage * mobilePageSize
                )
                .map(bill => renderMobileCard(bill))}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 16,
                  marginBottom: 16
                }}
              >
                <Pagination
                  current={mobilePage}
                  pageSize={mobilePageSize}
                  total={data.length}
                  onChange={(page) => setMobilePage(page)}
                  size="small"
                  showSizeChanger={false}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* View Modal - Responsive */}
      <Modal
        title={<Space><EyeOutlined /> Bill Details - {selectedBill?.bill_id}</Space>}
        open={isViewModal}
        onCancel={() => setIsViewModal(false)}
        width={isMobile ? '95%' : 800}
        footer={[
          <Button key="pdf" icon={<FilePdfOutlined />} style={{ color: '#ff4d4f' }} onClick={() => { setIsViewModal(false); downloadPDF(selectedBill); }}>Download PDF</Button>,
          <Button key="edit" type="primary" icon={<EditOutlined />} onClick={() => { setIsViewModal(false); handleEditClick(selectedBill); }}>Edit Bill</Button>,
          <Button key="close" onClick={() => setIsViewModal(false)}>Close</Button>
        ]}
      >
        {selectedBill && (
          <>
            <Descriptions bordered size="small" column={isMobile ? 1 : 2}>
              <Descriptions.Item label="Bill ID">{selectedBill.bill_id}</Descriptions.Item>
              <Descriptions.Item label="Bill Date">{selectedBill.bill_date}</Descriptions.Item>
              <Descriptions.Item label="Party Name">{selectedBill.party_name}</Descriptions.Item>
              <Descriptions.Item label="Mobile">{selectedBill.mobile || selectedBill.items?.[0]?.mobile || "-" || selectedBill.items?.[0]?.mobile || "-" || '-'}</Descriptions.Item>
              <Descriptions.Item label="Total Amount"><strong style={{ color: '#1890ff' }}>₹{parseFloat(selectedBill.total_amount).toLocaleString('en-IN')}</strong></Descriptions.Item>
              <Descriptions.Item label="Receipt Amount"><strong style={{ color: '#52c41a' }}>₹{parseFloat(selectedBill.receipt_amount || 0).toLocaleString('en-IN')}</strong></Descriptions.Item>
              <Descriptions.Item label="Balance Amount" span={2}><strong style={{ color: selectedBill.is_pending ? '#ff4d4f' : '#52c41a' }}>₹{(selectedBill.total_amount - (selectedBill.receipt_amount || 0)).toLocaleString('en-IN')}</strong></Descriptions.Item>
              <Descriptions.Item label="Status" span={2}><Badge status={selectedBill.is_pending ? "warning" : "success"} text={selectedBill.is_pending ? "Pending" : "Settled"} /></Descriptions.Item>
            </Descriptions>
            <Title level={5} style={{ marginTop: 16 }}>Service Items</Title>
            <Table dataSource={selectedBill.items} rowKey="id" size="small" pagination={false} scroll={{ x: isMobile ? 500 : undefined }}
              columns={[
                { title: '#', render: (_, __, idx) => idx + 1, width: 50 },
                { title: 'Vehicle No', dataIndex: 'vehicle_no', render: (text) => text || '-' },
                {
                  title: 'Chassis No',
                  render: (_, record) => {
                    const vehicle = allVehicles.find(
                      v => v.id === record.vehicle_id
                    );

                    return vehicle?.chassisNumber || '-';
                  }
                },
                { title: 'Amount', dataIndex: 'amount', render: (text) => `₹${parseFloat(text).toLocaleString('en-IN')}` },
                { title: 'Service Date', dataIndex: 'service_date' },
                { title: 'Description', dataIndex: 'description' }
              ]}
            />
          </>
        )}
      </Modal>

      {/* Settlement Modal - Responsive */}
      <Modal
        title={<Space><DollarOutlined style={{ color: '#52c41a' }} /> Settle Bill Amount</Space>}
        open={isSettleModal}
        onCancel={() => { setIsSettleModal(false); setSettlementAmount(0); setSelectedBillForSettle(null); }}
        width={isMobile ? '95%' : 500}
        footer={[
          <Button key="cancel" onClick={() => { setIsSettleModal(false); setSettlementAmount(0); setSelectedBillForSettle(null); }}>Cancel</Button>,
          <Button key="submit" type="primary" icon={<CheckCircleOutlined />} onClick={handleSettlement} loading={settling} style={{ background: '#52c41a' }}>Confirm Settlement</Button>
        ]}
      >
        {selectedBillForSettle && (
          <>
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Bill ID">{selectedBillForSettle.bill_id}</Descriptions.Item>
              <Descriptions.Item label="Party Name">{selectedBillForSettle.party_name}</Descriptions.Item>
              <Descriptions.Item label="Total Amount"><strong style={{ color: '#1890ff' }}>₹{parseFloat(selectedBillForSettle.total_amount).toLocaleString('en-IN')}</strong></Descriptions.Item>
              <Descriptions.Item label="Already Paid"><strong style={{ color: '#52c41a' }}>₹{parseFloat(selectedBillForSettle.receipt_amount || 0).toLocaleString('en-IN')}</strong></Descriptions.Item>
              <Descriptions.Item label="Balance Amount"><strong style={{ color: '#ff4d4f' }}>₹{(selectedBillForSettle.total_amount - (selectedBillForSettle.receipt_amount || 0)).toLocaleString('en-IN')}</strong></Descriptions.Item>
            </Descriptions>
            <Divider />
            <Text strong>Settlement Amount:</Text>
            <InputNumber style={{ width: '100%', marginTop: 8 }} prefix="₹" value={settlementAmount} onChange={(value) => setSettlementAmount(value)} min={1} max={selectedBillForSettle.total_amount - (selectedBillForSettle.receipt_amount || 0)} placeholder="Enter amount" size="large" formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={value => value.replace(/\$\s?|(,*)/g, '')} />
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>Maximum: ₹{(selectedBillForSettle.total_amount - (selectedBillForSettle.receipt_amount || 0)).toLocaleString('en-IN')}</Text>
          </>
        )}
      </Modal>
    </div>
  );
};

export default BillReport;